/**
 * Special characters and their names.
 * 
 * Each entry in the sub arrays follows the format:
 * [HTML Symbol, HTML Entity, Label]
 * 
 * @type {Object}
 */
export const symbols = {
    Currency: [
        ['¢', 'cent', 'Cents'],
        ['€', 'euro', 'Euros'],
        ['£', 'pound', 'Pounds'],
        ['¥', 'yen', 'Yen'],
        ['¤', 'currency', 'Currency'],
    ],
    Math: [
        ['−', 'minus', 'Minus'],
        ['±', 'plusmn', 'Plus minus'],
        ['÷', 'divide', 'Divide'],
        ['×', 'times', 'Multiply'],
        ['¼', 'frac14', 'One quarter'],
        ['½', 'frac12', 'One half'],
        ['¾', 'frac34', 'Three quarters'],
        ['⅓', 'frac13', 'One third'],
        ['⅔', 'frac23', 'Two thirds'],
        ['⅛', 'frac18', 'One eighth'],
        ['⅜', 'frac38', 'Three eighths'],
        ['⅝', 'frac58', 'Five eighths'],
        ['⅞', 'frac78', 'Seven eighths'],
        ['⁄', 'frasl', 'Fraction slash'],
        ['≤', 'le', 'Less-than or equal to'],
        ['≥', 'ge', 'Greater-than or equal to'],
        ['≈', 'asymp', 'Almost equal'],
        ['≠', 'ne', 'Not equal'],
        ['∞', 'infin', 'Infinity'],
    ],
    Units: [
        ['°', 'deg', 'Degrees'],
        ['′', 'prime', 'Minutes / feet'],
        ['″', 'Prime', 'Seconds / inches'],
        ['§', 'sect', 'Section'],
        ['¶', 'para', 'Paragraph'],
    ],
    Quotations: [
        ['‹', 'lsaquo', 'Single left-pointing'],
        ['›', 'rsaquo', 'Single right-pointing'],
        ['«', 'laquo', 'Left-pointing guillemet'],
        ['»', 'raquo', 'Right-pointing guillemet'],
        ['‘', 'lsquo', 'Left single'],
        ['’', 'rsquo', 'Right single'],
        ['“', 'ldquo', 'Left double'],
        ['”', 'rdquo', 'Right double'],
        ['‚', 'sbquo', 'Single low-9'],
        ['„', 'bdquo', 'Double low-9'],
    ],
    Punctuation: [
        ['¿', 'iquest', 'Inverted question mark'],
        ['¡', 'iexcl', 'Inverted exclamation mark'],
    ],
    Typography: [
        ['·', 'middot', 'Middle dot'],
        ['•', 'bull', 'Bullet'],
        ['…', 'hellip', 'Three-dot leader'],
        ['–', 'ndash', 'En dash'],
        ['—', 'mdash', 'Em dash'],
        ['¯', 'macr', 'Macron'],
        ['‾', 'oline', 'Overline'],
        ['¦', 'brvbar', 'Broken bar'],
    ],
    Accents: [
        ['À', 'Agrave', 'A grave'],
        ['Á', 'Aacute', 'A acute'],
        ['Â', 'Acirc', 'A circumflex'],
        ['Ã', 'Atilde', 'A tilde'],
        ['Ä', 'Auml', 'A diaeresis'],
        ['Å', 'Aring', 'A ring'],
        ['à', 'agrave', 'a grave'],
        ['á', 'aacute', 'a acute'],
        ['â', 'acirc', 'a circumflex'],
        ['ã', 'atilde', 'a tilde'],
        ['ä', 'auml', 'a diaeresis'],
        ['å', 'aring', 'a ring'],
        ['È', 'Egrave', 'E grave'],
        ['É', 'Eacute', 'E acute'],
        ['Ê', 'Ecirc', 'E circumflex'],
        ['Ë', 'Edia', 'E diaeresis'],
        ['è', 'egrave', 'e grave'],
        ['é', 'eacute', 'e acute'],
        ['ê', 'ecirc', 'e circumflex'],
        ['ë', 'edia', 'e diaeresis'],
        ['Ì', 'Igrave', 'I grave'],
        ['Í', 'Iacute', 'I acute'],
        ['Î', 'Icirc', 'I circumflex'],
        ['Ï', 'Idia', 'I diaeresis'],
        ['ì', 'igrave', 'i grave'],
        ['í', 'iacute', 'i acute'],
        ['î', 'icirc', 'i circumflex'],
        ['ï', 'idia', 'i diaeresis'],
        ['Ò', 'Ograve', 'O grave'],
        ['Ó', 'Oacute', 'O acute'],
        ['Ô', 'Ocirc', 'O circumflex'],
        ['Õ', 'Otilde', 'O tilde'],
        ['Ö', 'Ouml', 'O diaeresis'],
        ['Ø', 'Oslash', 'O slash'],
        ['ò', 'ograve', 'o grave'],
        ['ó', 'oacute', 'o acute'],
        ['ô', 'ocirc', 'o circumflex'],
        ['õ', 'otilde', 'o tilde'],
        ['ö', 'ouml', 'o diaeresis'],
        ['ø', 'oslash', 'o slash'],
        ['Ù', 'Ugrave', 'U grave'],
        ['Ú', 'Uacute', 'U acute'],
        ['Û', 'Ucirc', 'U circumflex'],
        ['Ü', 'Uuml', 'U diaeresis'],
        ['Ý', 'Yacute', 'Y acute'],
        ['Ÿ', 'Yuml', 'Y diaeresis'],
        ['ù', 'ugrave', 'u grave'],
        ['ú', 'uacute', 'u acute'],
        ['û', 'ucirc', 'u circumflex'],
        ['ü', 'uuml', 'u diaeresis'],
        ['ý', 'yacute', 'y acute'],
        ['ÿ', 'yuml', 'y diaeresis'],
        ['Ç', 'Ccedil', 'C cedilla'],
        ['Ñ', 'Ntilde', 'N tilde'],
        ['Š', 'Scaron', 'S caron'],
        ['THORN', 'THORN', 'THORN'],
        ['ç', 'ccedil', 'c cedilla'],
        ['ñ', 'ntilde', 'n tilde'],
        ['š', 'scaron', 's caron'],
        ['þ', 'thorn', 'thorn'],
    ],
    Ligature: [
        ['Æ', 'AElig', 'AE ligature'],
        ['Œ', 'OElig', 'OE ligature'],
    ],
    Greek: [
        ['Α', 'Alpha', 'Alpha'],
        ['Β', 'Beta', 'Beta'],
        ['Γ', 'Gamma', 'Gamma'],
        ['Δ', 'Delta', 'Delta'],
        ['Ε', 'Epsilon', 'Epsilon'],
        ['Ζ', 'Zeta', 'Zeta'],
        ['Η', 'Eta', 'Eta'],
        ['Θ', 'Theta', 'Theta'],
        ['Ι', 'Iota', 'Iota'],
        ['Κ', 'Kappa', 'Kappa'],
        ['Λ', 'Lambda', 'Lambda'],
        ['Μ', 'Mu', 'Mu'],
        ['Ν', 'Nu', 'Nu'],
        ['Ξ', 'Xi', 'Xi'],
        ['Ο', 'Omicron', 'Omicron'],
        ['Π', 'Pi', 'Pi'],
        ['Ρ', 'Rho', 'Rho'],
        ['Σ', 'Sigma', 'Sigma'],
        ['Τ', 'Tau', 'Tau'],
        ['Υ', 'Upsilon', 'Upsilon'],
        ['Φ', 'Phi', 'Phi'],
        ['Χ', 'Chi', 'Chi'],
        ['Ψ', 'Psi', 'Psi'],
        ['Ω', 'Omega', 'Omega'],
        ['α', 'alpha', 'alpha'],
        ['β', 'beta', 'beta'],
        ['γ', 'gamma', 'gamma'],
        ['δ', 'delta', 'delta'],
        ['ε', 'epsilon', 'epsilon'],
        ['ζ', 'zeta', 'zeta'],
        ['η', 'eta', 'eta'],
        ['θ', 'theta', 'theta'],
        ['ι', 'iota', 'iota'],
        ['κ', 'kappa', 'kappa'],
        ['λ', 'lambda', 'lambda'],
        ['μ', 'mu', 'mu'],
        ['ν', 'nu', 'nu'],
        ['ξ', 'xi', 'xi'],
        ['ο', 'omicron', 'omicron'],
        ['π', 'pi', 'pi'],
        ['ρ', 'rho', 'rho'],
        ['ς', 'sigmaf', 'Final sigma'],
        ['σ', 'sigma', 'sigma'],
        ['τ', 'tau', 'tau'],
        ['υ', 'upsilon', 'upsilon'],
        ['φ', 'phi', 'phi'],
        ['χ', 'chi', 'chi'],
        ['ψ', 'psi', 'psi'],
        ['ω', 'omega', 'omega'],
    ],
};
